import { app } from '@ircf/config';
import { API_ERROR_MESSAGES } from '@ircf/contracts';
import type { ApiErrorCode, ApiResponse, Paginated } from '@ircf/contracts';

/**
 * The single HTTP client.
 *
 * Everything the browser sends to the platform goes through `request`. That
 * matters for three reasons:
 *  - the session cookie policy (`credentials: 'include'`) is set in exactly one
 *    place, and no code path can accidentally omit it;
 *  - the platform response envelope is unwrapped once, so components receive
 *    domain objects rather than transport shapes;
 *  - a 401 has a single, consistent consequence.
 *
 * The base URL always targets `api.tame-dynamics.com/api/v1/<slug>`. `env.ts`
 * refuses to build if it is pointed at the `app.` host or at localhost in a
 * production build.
 */

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly fieldErrors: Record<string, string[]>;
  readonly details: unknown;

  constructor(options: {
    code: ApiErrorCode;
    status: number;
    message?: string;
    fieldErrors?: Record<string, string[]>;
    details?: unknown;
  }) {
    super(options.message ?? API_ERROR_MESSAGES[options.code]);
    this.name = 'ApiError';
    this.code = options.code;
    this.status = options.status;
    this.fieldErrors = options.fieldErrors ?? {};
    this.details = options.details;
  }

  /** True when retrying could plausibly succeed. */
  get isRetryable(): boolean {
    return this.code === 'NETWORK_ERROR' || this.status >= 500;
  }

  /** True when the user needs to authenticate again. */
  get isAuthFailure(): boolean {
    return this.code === 'UNAUTHENTICATED' || this.status === 401;
  }
}

type UnauthorisedHandler = () => void;

let unauthorisedHandler: UnauthorisedHandler | null = null;
let refreshPromise: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  refreshPromise ??= fetch(`${app.apiBaseUrl}/auth/refresh`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'include',
    })
      .then((response) => response.ok)
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  return refreshPromise;
}

/**
 * Registered once by the session provider. Called on any 401 so the app can
 * clear cached identity and redirect to sign-in preserving `returnTo`.
 */
export function setUnauthorisedHandler(handler: UnauthorisedHandler | null): void {
  unauthorisedHandler = handler;
}

/**
 * Query parameters.
 *
 * Typed as `object` rather than `Record<string, unknown>` on purpose: every query
 * in `endpoints.ts` is a declared interface (`SessionQuery`, `CheckInQuery`, …), and
 * an interface without an index signature is not assignable to a `Record`. Widening
 * here keeps the call sites strongly typed instead of forcing a cast at each one.
 */
export type QueryParams = object;

/**
 * Serialises one query value, or `null` when it must be dropped.
 *
 * Scalars and Dates only, deliberately. A bare `String(value)` would turn an
 * object into `?filter=[object Object]` - a request that succeeds, returns the
 * wrong data, and gives no clue why. Dates are forced to ISO because the wire
 * format is UTC ISO everywhere and `String(date)` yields a locale string.
 */
function toQueryValue(value: unknown): string | null {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString();
  return null;
}

/** Serialises query parameters, dropping empty values and expanding arrays. */
export function toQueryString(params: QueryParams | undefined): string {
  if (!params) return '';
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params) as [string, unknown][]) {
    if (value === undefined || value === null || value === '') continue;

    if (Array.isArray(value)) {
      // Repeated keys rather than a comma-joined value, so a filter value that
      // legitimately contains a comma is not silently split server-side.
      for (const item of value as unknown[]) {
        const serialised = toQueryValue(item);
        if (serialised !== null) search.append(key, serialised);
      }
      continue;
    }

    const serialised = toQueryValue(value);
    if (serialised !== null) search.append(key, serialised);
  }

  const serialised = search.toString();
  return serialised.length > 0 ? `?${serialised}` : '';
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: QueryParams;
  signal?: AbortSignal;
  /** Suppresses the global 401 handler, e.g. for the initial `/me` probe. */
  allowUnauthenticated?: boolean;
  /** Expect a non-JSON body, e.g. an ICS or CSV download. */
  responseType?: 'json' | 'text';
}

function isApiResponse(value: unknown): value is ApiResponse<unknown> {
  return typeof value === 'object' && value !== null && 'success' in value;
}

function normaliseErrorCode(status: number, code: unknown): ApiErrorCode {
  if (typeof code === 'string' && code in API_ERROR_MESSAGES) return code as ApiErrorCode;

  switch (status) {
    case 400:
      return 'VALIDATION_FAILED';
    case 401:
      return 'UNAUTHENTICATED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 409:
      return 'CONFLICT';
    case 413:
      return 'PAYLOAD_TOO_LARGE';
    case 415:
      return 'UNSUPPORTED_MEDIA_TYPE';
    case 422:
      return 'VALIDATION_FAILED';
    case 429:
      return 'RATE_LIMITED';
    default:
      return status >= 500 ? 'SERVER_ERROR' : 'SERVER_ERROR';
  }
}

async function parseBody(response: Response, responseType: 'json' | 'text'): Promise<unknown> {
  if (response.status === 204) return null;
  if (responseType === 'text') return response.text();

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    // A non-JSON body on an API route means something upstream intercepted the
    // request (a proxy error page, an HTML login redirect). Surface it as a
    // server error rather than throwing an opaque JSON parse failure.
    const text = await response.text();
    return text.length > 0 ? { success: false, error: { code: 'SERVER_ERROR', message: '' } } : null;
  }

  try {
    return (await response.json()) as unknown;
  } catch {
    throw new ApiError({ code: 'SERVER_ERROR', status: response.status });
  }
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const {
    method = 'GET',
    body,
    query,
    signal,
    allowUnauthenticated = false,
    responseType = 'json',
  } = options;

  const url = `${app.apiBaseUrl}${path}${toQueryString(query)}`;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const send = () =>
    fetch(url, {
      method,
      headers,
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });

  let response: Response;
  try {
    response = await send();
    if (response.status === 401 && !allowUnauthenticated && !path.startsWith('/auth/')) {
      if (await refreshSession()) response = await send();
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError({ code: 'NETWORK_ERROR', status: 0 });
  }

  if (response.status === 401 && !allowUnauthenticated) {
    unauthorisedHandler?.();
  }

  const payload = await parseBody(response, responseType);

  if (!response.ok) {
    const envelope = isApiResponse(payload) && payload.success === false ? payload.error : null;
    throw new ApiError({
      code: normaliseErrorCode(response.status, envelope?.code),
      status: response.status,
      // Only trust a server message when the envelope supplied one; otherwise
      // fall back to our own user-safe copy.
      message: envelope?.message && envelope.message.length > 0 ? envelope.message : undefined,
      fieldErrors: envelope?.fieldErrors,
      details: envelope?.details,
    });
  }

  if (responseType === 'text') return payload as T;

  if (isApiResponse(payload)) {
    if (payload.success) {
      if (payload.meta?.pagination && Array.isArray(payload.data)) {
        return { items: payload.data, pagination: payload.meta.pagination } as T;
      }
      return payload.data as T;
    }
    throw new ApiError({
      code: normaliseErrorCode(response.status, payload.error.code),
      status: response.status,
      message: payload.error.message,
      fieldErrors: payload.error.fieldErrors,
      details: payload.error.details,
    });
  }

  // Tolerate a bare (unenveloped) body so a platform response-format change
  // degrades to working rather than to a blank screen.
  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),

  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
} as const;

/**
 * Uploads a file to a presigned URL.
 *
 * Deliberately not routed through `request`: the PUT goes to object storage, not
 * to the platform API, and must not carry credentials or the API envelope.
 */
export async function uploadToSignedUrl(
  uploadUrl: string,
  file: File | Blob,
  requiredHeaders: Record<string, string>,
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: requiredHeaders,
    body: file,
    // Explicitly omitted: presigned URLs authenticate the request themselves,
    // and sending cookies to the storage host would be a needless leak.
    credentials: 'omit',
  });

  if (!response.ok) {
    throw new ApiError({
      code: response.status === 413 ? 'PAYLOAD_TOO_LARGE' : 'SERVER_ERROR',
      status: response.status,
      message: 'The file could not be uploaded. Please try again.',
    });
  }
}

/** Extracts the pagination-friendly shape from a list response. */
export function firstPageOf<T>(page: Paginated<T>): T[] {
  return page.items;
}
